import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGE_CODES,
  resolveSupportedLanguage,
} from './constants/languages';
import { applyDocumentLanguage } from './utils/documentLanguage';
import { loadFontsForLanguage } from './utils/localeFonts';
import { isEmbed, getEmbedLocale } from './config/embed';

// Capture the stored preference before init, because LanguageDetector's
// `localStorage` cache overwrites `vm_language` with the querystring value
// during init. We need the pre-init value to tell whether `?lang=` actually
// changed the language or just matched what was already stored.
const storedLanguageBeforeInit = resolveSupportedLanguage(
  window.localStorage.getItem('vm_language')
);

// A partner embed carries no `?lang=`, and its localStorage is frequently
// partitioned away by the browser, so the loader's `locale` is the only signal
// for the language the partner configured. It wins at load: i18next caches
// whatever it resolves, so deferring to the cached value would let a stale
// value outlive a partner changing `data-locale`. The in-widget selector still
// switches language for the session. Outside embeds this stays null and
// detection is unchanged.
const embedLanguage = isEmbed() ? resolveSupportedLanguage(getEmbedLocale() ?? null) : null;

i18n
  .use(
    resourcesToBackend(
      (language: string, namespace: string) => import(`./locales/${language}/${namespace}.json`)
    )
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    ...(embedLanguage ? { lng: embedLanguage } : {}),
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    // Load only the resolved language, not its base subtag too — otherwise
    // `zh-Hans` also requests a bare `zh` catalog that doesn't exist.
    load: 'currentOnly',
    ns: ['common', 'chat', 'ballot', 'landing', 'settings', 'signup', 'auth', 'help'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // `querystring` first lets an inbound `?lang=` win over a stored
      // preference; unsupported codes fall through to `fallbackLng`.
      order: ['querystring', 'localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'vm_language',
      // Resolve detected tags so the detector and the selectors agree on one
      // spelling. (The old `lng.split('-')[0]` turned `zh-TW` into an
      // unsupported bare `zh`.) Unresolvable tags pass through for
      // `supportedLngs` to reject.
      convertDetectedLanguage: (lng: string) => resolveSupportedLanguage(lng) ?? lng,
    },
  });

// Keep `<html lang>`/`dir` and the per-script webfonts on the active language.
// Also run inline: detection resolves synchronously during `init`, so the event
// for that first resolution has already fired by the time we subscribe.
i18n.on('languageChanged', (lng: string) => {
  applyDocumentLanguage(lng);
  loadFontsForLanguage(lng);
});
applyDocumentLanguage(i18n.language);
loadFontsForLanguage(i18n.language);

// Feature flagging param reading
export const urlLanguageOverride = resolveSupportedLanguage(
  new URLSearchParams(window.location.search).get('lang')
);

// True only when a valid `?lang=` actually changed the active language, i.e. it
// differs from the preference that was stored before init. Lets analytics record
// a genuine URL-driven change without inflating on `?lang=` that just matched the
// already-stored preference.
export const urlLanguageChangedActiveLanguage = Boolean(
  urlLanguageOverride && urlLanguageOverride !== storedLanguageBeforeInit
);

/**
 * Switch the active language.
 *
 * Kept as a thin wrapper so all programmatic language changes (login profile
 * sync in AuthProvider, settings load, cross-tab sync) route through a single
 * choke point rather than calling i18n.changeLanguage directly.
 */
export const changeLanguage = async (lng: string): Promise<unknown> => {
  // Preload the target language's catalogs before switching. Namespaces are
  // lazy-loaded per language (resourcesToBackend + load: 'currentOnly'), so
  // switching to an unloaded language makes react-i18next suspend the whole
  // routed tree to the PageLoader fallback — which tore down the open ballot
  // sheet and any open menu mid-interaction (the language-chip flicker).
  // Loading first keeps the active language rendered, so the switch is
  // synchronous and nothing suspends.
  try {
    await i18n.loadLanguages(lng);
  } catch {
    // Fall through: changeLanguage will load + suspend if the preload failed.
  }
  return i18n.changeLanguage(lng);
};

export default i18n;
